import type { components } from '@connected/api-client/api.generated';

import { useAuthentication } from '@connected/api-client/hooks/use-authentication';
import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { Button } from '@connected/ui/components/button';
import { Carousel, CarouselContent, CarouselItem } from '@connected/ui/components/carousel';
import { Image } from '@connected/ui/components/image';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Archive } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Breadcrumb } from '@/components/breadcrumb';
import { ComingSoonModal } from '@/components/coming-soon-modal';
import { DataTable } from '@/components/data-table';
import { LoadingPage } from '@/components/loading-page';
import { ObservationLogs } from '@/components/logbook-observations';
import { Observation } from '@/components/observation';

export const Route = createFileRoute(
    '/_public/customer/locations/$locationId/electrical/projects/$projectId/_overview/pv-installations/$pvInverterId/observations/$observationId/',
)({
    component: RouteComponent,
});

type ListNameValue = {
    Name: string | null;
    Value: string | Date | number | null | boolean;
    HexColorCode?: string;
};

type ListSeverityCode = components['schemas']['ListElectricalProjectObservationQueryResultObservation']['Code'] & {
    Value: string | null;
};

function RouteComponent() {
    const auth = useAuthentication();
    const { t } = useTranslation('customer-module-electrical');
    const params = Route.useParams();

    const projectQuery = useQuery(portaalQueryOptions.electrical.pvInverter.projectView({
        ElectricalProjectPVInverterId: params.pvInverterId,
    }));

    const [comingSoonModalOpen, setComingSoonModalOpen] = useState<boolean>(false);

    const observation = useQuery(portaalQueryOptions.electrical.observations.view({
        ElectricalProjectObservationId: params.observationId ?? null,
    }));

    const objectQuery = useQuery({
        ...portaalQueryOptions.portal.objects.view({ ObjectId: params.locationId }),
        enabled: !!auth.isAuthenticated && !!params.locationId,
    });

    const projectViewQuery = useQuery({
        ...portaalQueryOptions.electrical.project.view({ ElectricalProjectId: params.projectId }),
        enabled: !auth.isAuthenticated && !!params.projectId,
    });

    const severity = useMemo<ListSeverityCode[]>(() => [{
        Kind: 'Severe',
        Value: t('module-electrical.overview.observations-table.columns.severe'),
    }, {
        Kind: 'Serious',
        Value: t('module-electrical.overview.observations-table.columns.serious'),
    }, {
        Kind: 'Minor',
        Value: t('module-electrical.overview.observations-table.columns.minor'),
    }, {
        Kind: 'Remark',
        Value: t('module-electrical.overview.observations-table.columns.remarks'),
    }], [t]);

    const nameValueColumns = useMemo(() => [
        {
            accessorKey: 'Name',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.distribution-boards.list.name')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListNameValue } }) => (
                <DataTable.Cell variant="medium">
                    <div className="md:min-w-48 md:max-w-48 2xl:min-w-96 2xl:max-w-96 flex items-center min-h-8">
                        {row?.original?.Name}
                    </div>
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Value',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.distribution-boards.distribution-board.measurements.currents-table.header.value')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListNameValue } }) => (
                <DataTable.Cell variant="medium">
                    <div className="md:min-w-48 md:max-w-48 2xl:min-w-96 2xl:max-w-96 flex items-center min-h-8">
                        {row?.original?.Value?.toString()}
                        {row?.original?.HexColorCode && (
                            <Observation className="ml-4" children label={row?.original?.HexColorCode} color={row?.original?.HexColorCode} />
                        )}
                    </div>
                </DataTable.Cell>
            ),
        },
    ], [t]);
    if (projectQuery.isLoading || observation.isLoading) {
        return <LoadingPage />;
    }

    return (
        <div className="max-w-screen-2xl flex flex-col flex-1 gap-5 pb-0 overflow-y-auto h-full">
            <div>
                {projectQuery.isFetched && (
                    <Breadcrumb
                        items={[
                            {
                                name: auth.isAuthenticated
                                    ? objectQuery.data?.Object?.Name || t('module-electrical.projects.title')
                                    : projectViewQuery.data?.Project?.ProjectObject?.Name || t('module-electrical.projects.title'),
                                to: '/customer/locations/$locationId',
                                params: { locationId: params.locationId },
                            },
                            {
                                name: t('module-electrical.name'),
                                to: '/customer/locations/$locationId/electrical/projects/$projectId/overview',
                                params: { ...params },
                            },
                            {
                                name: t('module-electrical.pv-installations.title'),
                                to: '/customer/locations/$locationId/electrical/projects/$projectId/pv-installations',
                                params: { ...params },
                            },
                            {
                                name: projectQuery.data?.PVInverter?.Identifier ?? '',
                                to: '/customer/locations/$locationId/electrical/projects/$projectId/pv-installations/$pvInverterId/',
                                params: { ...params },
                            },
                            {
                                isActive: true,
                                name: t('module-electrical.report.observations.title'),
                            },
                        ]}
                    />
                )}
                <div className="font-semibold flex justify-between">
                    <div className="text-xl">
                        {projectQuery?.data?.PVInverter?.Identifier}
                    </div>
                    <div className="flex flex-row gap-2">
                        <Button
                            variant="link"
                            className="flex items-center gap-1 border px-3 rounded-lg text-foreground hover:no-underline hover:bg-background"
                            onClick={() => setComingSoonModalOpen(true)}
                        >
                            <Archive className="w-5 h-5" />
                            {t('module-electrical.report.buttons.archive')}
                        </Button>
                        <ComingSoonModal open={comingSoonModalOpen} onOpenChange={setComingSoonModalOpen} />
                    </div>
                </div>
            </div>
            <div className="w-full h-px block bg-border-primary"></div>

            <div className="grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-2 md:grid-cols-3 md:gap-4">
                <div className="col-span-1 text-sm mb-4 md:mb-0">
                    <div className="font-semibold ">
                        {`${observation?.data?.Observation?.LocationRemark} ${t('module-electrical.distribution-boards.distribution-board.images.title').toLocaleLowerCase()}`}
                    </div>
                </div>
                <div className="col-span-2">
                    {observation?.data?.Observation?.Files && observation?.data?.Observation?.Files.length > 0
                        ? (
                                <Carousel loop>
                                    <CarouselContent className="-ml-2 md:-ml-4">
                                        {observation?.data?.Observation?.Files.map((file, idx) => (
                                            <CarouselItem className="pl-2 md:pl-4 basis-1/1 sm:basis-1/2 lg:basis-1/3 xl:basis-1/4" key={file.ElectricalProjectFileId ?? idx}>
                                                {file.File && (
                                                    <Image
                                                        previewer
                                                        className="h-52 w-auto object-contain rounded-lg"
                                                        alt={file?.File?.Name ?? ''}
                                                        queryOptions={portaalQueryOptions.files.electricalProject({
                                                            ElectricalProjectFileId: file.ElectricalProjectFileId!,
                                                            ImageQuality: 'OriginalWebp',
                                                        })}
                                                        enabled={!!file.ElectricalProjectFileId}
                                                    />
                                                )}
                                            </CarouselItem>
                                        ))}
                                    </CarouselContent>
                                </Carousel>
                            )
                        : (
                                <div className="flex items-center justify-center h-52 text-text-primary/50 rounded-lg border-2 border-dashed">
                                    <p>{t('module-electrical.pv-installations.messages.noImagesFound')}</p>
                                </div>
                            )}
                </div>

            </div>

            <div className="w-full h-px block bg-border-primary"></div>
            <div className="grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-2 md:grid-cols-3 md:gap-4">
                <div className="col-span-1 text-sm mb-4 md:mb-0">
                    <div className="font-semibold ">
                        {t('module-electrical.pv-installations.observationDetails')}
                    </div>
                </div>
                <div className="col-span-2">
                    <DataTable
                        columns={nameValueColumns}
                        data={[
                            {
                                Name: t('module-electrical.report.observations.title'),
                                Value: severity.find(row => row?.Kind === observation?.data?.Observation?.CodeKind)?.Value
                                    ?? t('module-electrical.pv-installations.messages.noObservation'),
                                HexColorCode: observation?.data?.Observation?.HexColorCode ?? '',
                            },
                            {
                                Name: t('module-electrical.report.observations.details-table.columns.date'),
                                Value: observation?.data?.Observation?.Date
                                    ? new Date(observation.data.Observation.Date).toLocaleDateString()
                                    : t('module-electrical.pv-installations.messages.noDate'),
                            },
                            {
                                Name: t('module-electrical.installation.location-description.title'),
                                Value: observation?.data?.Observation?.LocationRemark
                                    ? observation.data.Observation.LocationRemark
                                    : t('module-electrical.pv-installations.messages.noLocation'),
                            },
                            {
                                Name: t('module-electrical.installation.notifications-table.header.comment'),
                                Value: observation?.data?.Observation?.Remark
                                    ? observation.data.Observation.Remark
                                    : t('module-electrical.pv-installations.messages.noRemark'),
                            },
                            {
                                Name: t('module-electrical.pv-installations.table.recommendation'),
                                Value: observation?.data?.Observation?.Recommendation
                                    ? observation.data.Observation.Recommendation
                                    : t('module-electrical.pv-installations.messages.noRecommendation'),
                            },
                        ]}
                        pageSize={10}
                    />
                </div>
            </div>
            <div className="w-full">
                <ObservationLogs observationId={params.observationId} projectType="electrical" projectId={params.projectId} />
            </div>
        </div>
    );
}
